import React from 'react';
import { useTranslation } from 'react-i18next';

export default function SportSelectionStep({
  initialSport = null,
  initialVisibility = null,
  onBack,
  onNext
}) {
  const { t } = useTranslation();

  const [sport, setSport] = React.useState(initialSport);
  const [visibility, setVisibility] = React.useState(initialVisibility);

  const canContinue = Boolean(sport) && Boolean(visibility);

  return (
    <div className="max-w-2xl mx-auto p-8 bg-white shadow-2xl shadow-indigo-100/50 rounded-3xl mt-4 md:mt-10 border border-gray-100">
      <h2 className="text-3xl font-black mb-3 text-gray-900 tracking-tight">{t('wizard.sport.title')}</h2>
      <p className="text-gray-500 mb-8 font-medium">{t('wizard.sport.subtitle')}</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <button
          type="button"
          onClick={() => setSport('run')}
          className={[
            "text-left border-2 rounded-2xl p-6 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md",
            sport === 'run' 
              ? 'border-indigo-600 bg-indigo-50/50 ring-1 ring-indigo-600/20' 
              : 'border-gray-100 hover:border-indigo-300 hover:bg-indigo-50/30'
          ].join(' ')}
        >
          <div className={`text-lg font-black mb-1 ${sport === 'run' ? 'text-indigo-700' : 'text-gray-900'}`}>{t('wizard.sport.run.title')}</div>
          <div className="text-sm text-gray-500 font-medium leading-relaxed">{t('wizard.sport.run.subtitle')}</div>
        </button>

        <button
          type="button"
          onClick={() => setSport('bike')}
          className={[
            "text-left border-2 rounded-2xl p-6 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md",
            sport === 'bike' 
              ? 'border-indigo-600 bg-indigo-50/50 ring-1 ring-indigo-600/20' 
              : 'border-gray-100 hover:border-indigo-300 hover:bg-indigo-50/30'
          ].join(' ')}
        >
          <div className={`text-lg font-black mb-1 ${sport === 'bike' ? 'text-indigo-700' : 'text-gray-900'}`}>{t('wizard.sport.bike.title')}</div>
          <div className="text-sm text-gray-500 font-medium leading-relaxed">{t('wizard.sport.bike.subtitle')}</div>
        </button>

        <button
          type="button"
          onClick={() => setSport('triathlon')}
          className={[
            "text-left border-2 rounded-2xl p-6 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md",
            sport === 'triathlon' 
              ? 'border-indigo-600 bg-indigo-50/50 ring-1 ring-indigo-600/20' 
              : 'border-gray-100 hover:border-indigo-300 hover:bg-indigo-50/30'
          ].join(' ')}
        >
          <div className={`text-lg font-black mb-1 ${sport === 'triathlon' ? 'text-indigo-700' : 'text-gray-900'}`}>{t('wizard.sport.triathlon.title')}</div>
          <div className="text-sm text-gray-500 font-medium leading-relaxed">{t('wizard.sport.triathlon.subtitle')}</div>
        </button>
      </div>

      <div className="mt-10 pt-8 border-t border-gray-100">
        <div className="text-sm font-black text-gray-900 uppercase tracking-wide mb-3">{t('wizard.visibility.title')}</div>
        <p className="text-gray-500 mb-4 text-sm font-medium">{t('wizard.visibility.description')}</p>
        <div className="grid grid-cols-1 gap-3">
          {(['unlisted', 'private']).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setVisibility(v)}
              className={[
                'text-left border-2 rounded-xl p-4 transition-all duration-200 cursor-pointer flex items-center justify-between group',
                visibility === v 
                  ? 'border-indigo-500 bg-indigo-50/50 shadow-inner' 
                  : 'border-gray-100 hover:border-gray-300 hover:bg-gray-50'
              ].join(' ')}
            >
              <div>
                <div className={`text-sm font-bold ${visibility === v ? 'text-indigo-900' : 'text-gray-700'}`}>{t(`wizard.visibility.${v}.title`)}</div>
                <div className="text-xs text-gray-500 font-medium mt-0.5">{t(`wizard.visibility.${v}.subtitle`)}</div>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${visibility === v ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300 group-hover:border-gray-400'}`}>
                {visibility === v && <div className="w-2 h-2 rounded-full bg-white" />}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-10 flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={() => onBack?.()}
          className="px-6 py-3 rounded-full font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors uppercase tracking-wide text-sm"
        >
          {t('wizard.previous')}
        </button>

        <button
          type="button"
          disabled={!canContinue}
          onClick={() => onNext?.({ sport, visibility })}
          className={[
            "px-8 py-3.5 rounded-full font-bold text-sm tracking-wide uppercase shadow-lg transition-all duration-200",
            canContinue 
              ? "bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-indigo-500/40 hover:-translate-y-0.5" 
              : "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none"
          ].join(' ')}
        >
          {t('wizard.next')}
        </button>
      </div>
    </div>
  );
}


